/* eslint-disable require-yield */
import { addHistoryAction } from 'actions/historyActions'
import { clearAssetsSelection, updateAssetInPreview } from 'actions/layer'
import { cancelAssetSettingsForm } from 'actions/mainView'
import { setProjectTimeCodeFormat } from 'actions/playback'
import { resetMarkers, setIOPointsData, setIOPointsHistoryData, setShowInOutPoints } from 'actions/preview'
import { setActiveInteractionUIElement } from 'actions/userInterface'
import { MARK_TYPE } from 'config/constants/preview'
import { TIME_CODE_CONTAINER_LOCATION } from 'config/constants/timecode'
import { UI_ELEMENT } from 'config/constants/ui'
import { PLAYER_TYPE, SOURCE_FILE_TYPES } from 'enums'
import { isEmpty, isNil, isNumber } from 'lodash'
import { IOPointsHistoryAction } from 'models/HistoryActions/IOPointsHistoryAction'
import { all, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import * as ActionTypes from '~/actions/ActionTypes'
import {
  setIsSliderOnBufferingPos,
  setTimelineAssetIds,
  deleteLayer,
  forceDeleteEmptyLayer,
  clearCopiedAssets,
  showSettings,
  setSliderTimeCenter,
  setReference,
  rewind
} from '~/actions/timeline'
import { getBackgroundAssets } from '~/helpers/getBackgroundAssets'
import { getComposedAssets } from '~/helpers/getComposedAssets'
import { getPlayingAssets } from '~/helpers/getPlayingAssets'
import { getTransitionAssets } from '~/helpers/getTransitionAssets'
import {
  getAssets,
  getAssetsByLayers,
  getTransitionsByAssetId,
  selectBackgroundAssetIds,
  selectComposedAssetIds,
  selectFlattenedVideoAssets,
  selectPlayingAssetIds,
  selectTransitionAssetIds,
  selectAssetById,
  getSelectedAssets,
  getReferenceVideoAsset
} from '~/selectors'
import { selectIsIgnoreLatestActionInHistory } from '~/selectors/historyActions'
import { secondsToTimelineTime, DEFAULT_FPS } from '~/Util'
import NotFoundMediaImage from '~/assets/projects/not_found_media.png'

const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b)
const toId = x => x.id

function* handleSliderUpdateOrRewind({ payload }) {
  const sliderProgress = yield select(state => state.timeline.sliderTime)
  const sliderTime = payload?.time ?? sliderProgress
  const allAssets = yield select(getAssets)
  const flattenedVideoAssets = yield select(selectFlattenedVideoAssets)
  const assetsByLayers = yield select(getAssetsByLayers)
  const backgroundAssetIds = yield select(selectBackgroundAssetIds)
  const transitionAssetIds = yield select(selectTransitionAssetIds)
  const composedAssetIds = yield select(selectComposedAssetIds)
  const playingAssetIds = yield select(selectPlayingAssetIds)

  const newPlayingAssets = getPlayingAssets({ sliderTime, assetsByLayers })
  const newPlayingAssetIds = newPlayingAssets.map(toId)

  const transitions = yield select(state => (
    newPlayingAssetIds.slice().reverse().reduce((acc, id) => {
      const assetTransitions = getTransitionsByAssetId(state, id)
      return assetTransitions.length ? [ ...acc, ...assetTransitions ] : acc
    }, [])
  ))

  const newTransitions = getTransitionAssets({
    sliderTime,
    transitions,
  })

  const newTransitionAssetIds = newTransitions.map(toId)

  const newComposedAssetIds = getComposedAssets({
    allAssets,
    transitions: newTransitions,
    playingMediaAssetIds: newPlayingAssetIds,
  }).map(toId)

  const newBackgroundAssetIds = getBackgroundAssets({
    sliderTime,
    playingMediaAssetIds: newPlayingAssetIds,
    flattenedVideoAssets,
  }).map(toId)

  if (!isEqual(backgroundAssetIds, newBackgroundAssetIds)
  || !isEqual(playingAssetIds, newPlayingAssetIds)
  || !isEqual(transitionAssetIds, newTransitionAssetIds)
  || !isEqual(composedAssetIds, newComposedAssetIds)) {
    yield put(setTimelineAssetIds({
      playingAssetIds: newPlayingAssetIds,
      backgroundAssetIds: newBackgroundAssetIds,
      composedAssetIds: newComposedAssetIds,
      transitionAssetIds: newTransitionAssetIds,
    }))
  }
}

function* handleSliderUpdate() {
  const isSliderOnBufferingPos = yield select(state => state.timeline.isSliderOnBufferingPos)
  if (isSliderOnBufferingPos) {
    yield put(setIsSliderOnBufferingPos(false))
  }
}

function* deleteEmptyLayers({ payload }) {
  const { recordInHistory } = payload ?? {}
  const layers = yield select(state => state.timeline.layers)
  const ignoreLatestActionInHistory = yield select(selectIsIgnoreLatestActionInHistory)
  const assetsByLayers = yield select(getAssetsByLayers)

  if (ignoreLatestActionInHistory) return

  for (const [ index, layer ] of layers.entries()) {
    const assets = assetsByLayers.get(layer.id)
    if (assets.length === 0 && index !== layers.length - 1) {
      yield put(deleteLayer(layer.id, false, true, recordInHistory))
    }
  }
}

function* deleteEmptyLayersWithoutHistory() {
  const layers = yield select(state => state.timeline.layers)
  const assetsByLayers = yield select(getAssetsByLayers)

  const ids = []
  for (const layer of layers.values()) {
    const assets = assetsByLayers.get(layer.id)
    if (assets.length === 0) {
      ids.push(layer.id)
    }
  }
  yield put(forceDeleteEmptyLayer(ids))
}

function* handleUpdateTimelineState() {
  yield put(clearCopiedAssets())
}

function* applyIOPoints({ payload }) {
  const { asset } = payload
  const { markIn, markOut } = yield select(state => state.preview.clipCreator.media)
  const playbackFileID = yield select(state => state.playback.selectedClipId)
  // io points apply only to a single asset
  if (playbackFileID !== asset[0]?.fileId || asset.length > 1) {
    return
  }
  const progress = yield select(state => state.preview.playerProgress.source)
  const initialStartTime = asset[0].startTime
  const mediaStart = !isNil(markIn) ? secondsToTimelineTime(markIn) : asset[0].mediaStart
  const duration = !isNil(markOut) ? secondsToTimelineTime(markOut) : asset[0].duration
  const patch = {
    startTime: initialStartTime,
    mediaStart,
    duration,
    endTime: initialStartTime + duration - mediaStart,
    playerProgress: secondsToTimelineTime(progress),
  }
  yield put(updateAssetInPreview(asset[0].id, patch))
  yield put(resetMarkers({ preview: PLAYER_TYPE.MEDIA }))
}

function* setIOPointsForSelectedAsset({ type, payload }) {
  const { showIOPoints, preview,
    updateAsset, point, initProps = {} } = payload
  const ioPoints = yield select(state => state.preview.clipCreator)
  const prevShowIOPoints = yield select(state => state.preview.showInOutPoints)
  const isMoving = yield select(state => state.preview.inOutPointsMoving)

  let historyProperties = {
    prevPreviewAssetProps: {},
    nextPreviewAssetProps: {},
    prevIOPoints: { ...ioPoints },
    nextIOPoints: { ...ioPoints },
    prevShowInOutPoints: { ...prevShowIOPoints },
    nextShowInOutPoints: { ...prevShowIOPoints },
    assetId: null,
    preview,
  }

  if (type === ActionTypes.SET_PREVIEW_MARKERS) {
    historyProperties = {
      ...historyProperties,
      nextIOPoints: { ...ioPoints, [preview]: payload.data[preview] },
      nextShowInOutPoints: { ...prevShowIOPoints, [preview]: showIOPoints },
    }
  }

  if (type === ActionTypes.SET_PREVIEW_MARKER) {
    historyProperties = {
      ...historyProperties,
      nextIOPoints: { ...ioPoints,
        [preview]: { ...ioPoints[preview], [payload.markType]: payload.point } },
      nextShowInOutPoints: { ...prevShowIOPoints, [preview]: showIOPoints },
    }
  }
  let patch = {}
  let previewAsset = null
  if (preview === PLAYER_TYPE.MEDIA) {
    const activePreview = yield select(state => state.preview.activePreview)
    const inOutPointsTimeLineAssetId = yield select(state => state.preview.inOutPointsTimeLineAsset)
    previewAsset = yield select(state => selectAssetById(state, inOutPointsTimeLineAssetId))
    if (previewAsset) {
      const selectedClipId = yield select(state => state.playback.selectedClipId)
      if (previewAsset.fileId === selectedClipId) {
        if (type === ActionTypes.SET_PREVIEW_MARKER) {
          if (payload.markType === MARK_TYPE.OUT) {
            patch = {
              duration: isNumber(point)
                ? secondsToTimelineTime(point) - previewAsset.mediaStart
                : previewAsset.duration,
            }
          }
          if (payload.markType === MARK_TYPE.IN) {
            const mediaStart = secondsToTimelineTime(point)
            const diff = mediaStart - previewAsset.mediaStart
            patch = {
              startTime: isNumber(point)
                ? previewAsset.startTime + diff
                : previewAsset.startTime,
              mediaStart: isNumber(point)
                ? mediaStart
                : previewAsset.mediaStart,
              duration: previewAsset.duration - diff,
            }
          }
        } else if (type === ActionTypes.SET_PREVIEW_MARKERS) {
          const { markOut, markIn } = payload?.data[activePreview] ?? {}
          const markOutTimeLineUnitSec = secondsToTimelineTime(markOut)
          const markInTimeLineUnitSec = secondsToTimelineTime(markIn)
          const duration = markOutTimeLineUnitSec - markInTimeLineUnitSec
          patch = {
            duration,
            mediaStart: markInTimeLineUnitSec,
            startTime: previewAsset.startTime,
            endTime: previewAsset.startTime + duration,
          }
        }
      }
    }
  }
  if (previewAsset) {
    historyProperties.nextPreviewAssetProps = { ...patch }
    historyProperties.prevPreviewAssetProps = {
      duration: initProps.duration ?? previewAsset.duration,
      mediaStart: initProps.mediaStart ?? previewAsset.mediaStart,
      startTime: initProps.startTime ?? previewAsset.startTime,
      endTime: initProps.endTime ?? previewAsset.endTime,
    }
    historyProperties.assetId = previewAsset.id
  }
  yield put(setIOPointsData(historyProperties.nextIOPoints, historyProperties.nextShowInOutPoints))
  yield put(setShowInOutPoints(!!showIOPoints, preview))
  if (!isEmpty(patch) && (isNil(updateAsset) || updateAsset)) {
    yield put(updateAssetInPreview(previewAsset.id, patch, { historyAction: false }))
  }
  const ioPointsHistoryData = yield select(state => state.preview.inOutPointsHistoryData)

  if (!isEmpty(ioPointsHistoryData.prevIOPoints)) {
    delete historyProperties.prevIOPoints
  }
  if (!isEmpty(ioPointsHistoryData.prevPreviewAssetProps)) {
    delete historyProperties.prevPreviewAssetProps
  }
  if (isMoving) {
    yield put(setIOPointsHistoryData(historyProperties))
  } else {
    yield put(addHistoryAction(IOPointsHistoryAction, { historyProperties }))
  }
}

function* handleShowAssetSettings({ type, payload }) {
  if ([ ActionTypes.SAVE_SETTINGS_VISIBILITY_CHANGED ].includes(type)) {
    yield put(cancelAssetSettingsForm())
    return
  }
  const { modifiers } = payload ?? {}
  const { selectSame } = modifiers ?? {}
  const isShowAssetSettings = yield select(state => Boolean(state.mainView.showAssetSettings))
  if (!selectSame) {
    yield put(cancelAssetSettingsForm())
  }
  if (isShowAssetSettings) {
    yield put(showSettings())
  }
}

function* clearTimelineAssetsSelection() {
  const selectedAsset = yield select(getSelectedAssets)
  if (!isEmpty(selectedAsset)) {
    yield put(clearAssetsSelection())
  }
}

function* onChangeScrollLeft({ type }) {
  const { sliderTime } = yield select(state => state.timeline)
  yield put(setSliderTimeCenter(sliderTime, type === ActionTypes.SET_TIMELINE_SCALE))
}

function* resetFPS() {
  const assets = yield select(state => state.layer.assets)
  const { width, height } = yield select(getReferenceVideoAsset)
  if (isEmpty(assets)) {
    yield put(setReference({
      id: null,
      width,
      height,
      fpsNum: DEFAULT_FPS,
      fpsDenum: 1,
    }))
    yield put(setProjectTimeCodeFormat({
      mode: __CFG__.TIME_CODE_MODE.TIMELINE_TIME_CODE,
      containerLocation: TIME_CODE_CONTAINER_LOCATION.TIMELINE,
    }))
  }
}

function* onAssetAddedToTimeline({ payload }) {
  const allAssets = yield select(getAssets)
  if (!isEmpty(allAssets)) {
    const currentRefFileData = yield select(getReferenceVideoAsset)
    if (!isEqual(payload.referenceFileData, currentRefFileData)) {
      yield put(setReference(payload.referenceFileData))
    }
  }
  yield put({
    type: ActionTypes.UPDATE_TIMELINE,
    payload,
  })
  const { asset, ignoreSliderTime } = payload
  if (!ignoreSliderTime && asset?.[0]) {
    yield put(rewind(asset[0].startTime))
  }
  yield put(setActiveInteractionUIElement(UI_ELEMENT.TIMELINE))
}

function* IOPointsResetHistory({ payload }) {
  const historyProperties = { ...payload.historyProperties }
  yield put(addHistoryAction(IOPointsHistoryAction, { historyProperties }))
}

function* updateAssetProperty({ payload, type }) {
  switch (type) {
    case ActionTypes.SOURCE_FILE_DELETED: {
      if (payload?.mediaPool !== SOURCE_FILE_TYPES.PROJECT_MEDIA) {
        yield put({
          type: ActionTypes.ASSET_PROPERTY_UPDATE,
          payload: {
            ...payload,
            newData: {
              thumbnail: NotFoundMediaImage,
              mediaDeleted: true,
              errorMessage: 'Media file not found',
            },
          },
        })
      }
      break
    }
    default:
      break
  }
}


function* watchAll() {
  yield all([
    takeEvery([
      ActionTypes.TIMELINE_REWINDED,
      ActionTypes.UPDATE_PLAYING_ASSETS,
      ActionTypes.TRANSITION_ATTACHED_TO_ASSET,
      ActionTypes.DELETE_ASSETS,
      ActionTypes.RIPPLE_DELETE_ASSETS,
      ActionTypes.RIPPLE_TRIM,
      ActionTypes.UNDO_RIPPLE_TRIM,
      ActionTypes.ASSET_MOVED_ON_TIMELINE,
      ActionTypes.ASSET_ADDED_TO_TIMELINE,
      ActionTypes.COPIED_ASSET_ADDED_TO_TIMELINE,
      ActionTypes.ASSET_PREVIEW_UPDATED,
      ActionTypes.LAYER_TOGGLE_VISIBLE,
      ActionTypes.UPDATE_TIMELINE ], handleSliderUpdateOrRewind),
    takeEvery(ActionTypes.SET_COPIED_TIME_CODE, handleUpdateTimelineState),
    takeEvery([ ActionTypes.UPDATE_SLIDER ], handleSliderUpdate),
    takeLatest([
      ActionTypes.ASSET_ADDED_TO_TIMELINE,
    ], applyIOPoints),
    takeEvery([
      ActionTypes.SET_PREVIEW_MARKER,
      ActionTypes.SET_PREVIEW_MARKERS,
    ], setIOPointsForSelectedAsset),
    takeEvery([
      ActionTypes.ASSET_MOVED_ON_TIMELINE,
      ActionTypes.DELETE_ASSETS,
      ActionTypes.RIPPLE_DELETE_ASSETS,
      ActionTypes.RIPPLE_TRIM,
    ], deleteEmptyLayers),
    takeEvery([
      ActionTypes.MULTIPLE_ASSETS_MOVED_ON_TIMELINE,
      ActionTypes.UNDO_MULTIPLE_MOVE_ASSETS,
      ActionTypes.LOAD_PROJECT,
    ], deleteEmptyLayersWithoutHistory),
    takeEvery([
      ActionTypes.SAVE_SETTINGS_VISIBILITY_CHANGED,
      ActionTypes.ASSET_SELECTED,
    ], handleShowAssetSettings),
    takeEvery([
      ActionTypes.RESET_MARK,
    ], IOPointsResetHistory),
    takeLatest([
      ActionTypes.SET_PREVIEW_MARKER,
      ActionTypes.SET_PREVIEW_MARKERS,
      ActionTypes.RESET_MARK,
    ], clearTimelineAssetsSelection),
    takeLatest([
      ActionTypes.SET_RESIZABLE_PARAMS,
      ActionTypes.SET_TIMELINE_SCALE,
    ], onChangeScrollLeft),
    takeLatest([
      ActionTypes.DELETE_ASSETS,
    ], resetFPS),
    takeEvery([ ActionTypes.ASSET_ADDED_TO_TIMELINE ], onAssetAddedToTimeline),
    takeEvery([
      ActionTypes.SOURCE_FILE_DELETED,
    ], updateAssetProperty),
  ])
}

export default watchAll
